import { css } from '@styled-system/css';
import { Fragment, useMemo, type FC } from 'react';
import { useTranslation } from 'react-i18next';

import { useTheme } from '@/app/providers/ThemeProvider';
import { Message } from '@/shared/components/ui/Message';
import { Tooltip } from '@/shared/components/ui/Tooltip';
import { dsTokens } from '@/shared/theme/tokens';

// ============================================================================
// Meilleurs moments — deux heatmaps calendrier côte à côte :
// « Quand vendre ? » (heat des ventes) et « Quand botter ? » (heat des mises
// en ligne). 7 jours × 6 créneaux (6h → 24h), pur CSS grid, palette DS.
// ============================================================================

export interface WeeklySlotPoint {
  /** 0 = lundi … 6 = dimanche */
  day: number;
  /** 0 = 6h-9h … 5 = 21h-24h */
  slot: number;
  sales: number;
  added: number;
}

interface BestMomentsHeatmapProps {
  /** 'vendor' : une seule grille « Quand met-il en vente ? ». */
  variant?: 'product' | 'vendor';
  data: WeeklySlotPoint[] | null;
  loading?: boolean;
}

// Créneaux serveur : 6 buckets de 4h couvrant 24h (0..5)
const SLOT_LABELS = ['0–4h', '4–8h', '8–12h', '12–16h', '16–20h', '20–24h'];

// Convention serveur : day 0 = dimanche … 6 = samedi. Affichage lundi-first.
const DAY_DISPLAY_ORDER = [1, 2, 3, 4, 5, 6, 0];
const TOP_COUNT = 3;

// Lundi 2024-01-01 → libellés de jours localisés
const dayLabel = (day: number, locale: string): string =>
  new Date(2024, 0, 7 + day).toLocaleDateString(locale, { weekday: 'short' }); // 2024-01-07 = dimanche (day 0)

const row = css({
  display: 'flex',
  gap: '8',
  flexWrap: 'wrap',
});

const half = css({
  flex: 1,
  minWidth: '360px',
  display: 'flex',
  flexDirection: 'column',
  gap: '4',
});

const heatmapTitle = css({
  fontSize: 'body',
  fontWeight: 'semibold',
  color: 'text.primary',
});

const bestWorstRow = css({
  display: 'flex',
  alignItems: 'center',
  gap: '5',
  flexWrap: 'wrap',
  fontSize: 'caption',
  color: 'text.tertiary',
});

const bestWorstItem = css({
  display: 'inline-flex',
  alignItems: 'baseline',
  gap: '2',
});

const bestWorstValue = css({
  fontFamily: 'mono',
  fontWeight: 'semibold',
  color: 'text.primary',
  textTransform: 'capitalize',
});

const grid = css({
  display: 'grid',
  gridTemplateColumns: '52px repeat(6, 1fr)',
  gap: '2',
  alignItems: 'stretch',
});

const colHeader = css({
  fontSize: 'micro',
  fontFamily: 'mono',
  color: 'text.tertiary',
  textAlign: 'center',
  paddingBottom: '1',
});

const rowHeader = css({
  fontSize: 'caption',
  color: 'text.secondary',
  display: 'flex',
  alignItems: 'center',
  textTransform: 'capitalize',
});

const cellStyle = css({
  height: '30px',
  borderRadius: 'md',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'border.subtle',
  cursor: 'default',
  transition: 'transform 100ms ease',
  width: '100%',
  _hover: { transform: 'scale(1.06)' },
});

const legendRow = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: '3',
  fontSize: 'micro',
  color: 'text.tertiary',
});

const legendCell = css({
  width: '14px',
  height: '14px',
  borderRadius: 'sm',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'border.subtle',
});

/** Alpha hex (00-FF) pour une intensité 0-1 — plancher léger pour rester visible. */
const alphaHex = (intensity: number): string => {
  const alpha = Math.round((0.08 + intensity * 0.92) * 255);
  return alpha.toString(16).padStart(2, '0').toUpperCase();
};

// ─── Une grille (un mode) ──────────────────────────────────────────────────

interface HeatmapGridProps {
  data: WeeklySlotPoint[];
  /** 'listing' : métrique des ajouts mais en couleur brand (heatmap vendeur). */
  mode: 'sell' | 'buy' | 'listing';
}

const HeatmapGrid: FC<HeatmapGridProps> = ({ data, mode }) => {
  const { t, i18n } = useTranslation();
  const { theme: colorMode } = useTheme();
  const ds = dsTokens[colorMode];
  const locale = i18n.language?.startsWith('en') ? 'en-GB' : 'fr-FR';

  const { cells, maxValue, topKeys, bestKey, worstKey } = useMemo(() => {
    const map = new Map<string, number>();
    data.forEach((point) => {
      map.set(`${point.day}-${point.slot}`, mode === 'sell' ? point.sales : point.added);
    });
    const max = Math.max(1, ...map.values());
    const sortedEntries = [...map.entries()].sort((a, b) => b[1] - a[1]);
    const top = sortedEntries.slice(0, TOP_COUNT).map(([key]) => key);
    return {
      cells: map,
      maxValue: max,
      topKeys: new Set(top),
      bestKey: sortedEntries[0]?.[0] ?? null,
      worstKey: sortedEntries[sortedEntries.length - 1]?.[0] ?? null,
    };
  }, [data, mode]);

  const heatColor = mode === 'buy' ? ds['warning.default'] : ds['brand.primary'];

  const slotText = (key: string | null): string => {
    if (!key) return '—';
    const [day, slot] = key.split('-').map(Number);
    return `${dayLabel(day, locale)} ${SLOT_LABELS[slot]}`;
  };

  return (
    <>
      {/* Résumé texte : meilleur et pire créneau */}
      <div className={bestWorstRow}>
        <span className={bestWorstItem}>
          {t('statsPanel.bestSlot')}
          <span className={bestWorstValue}>{slotText(bestKey)}</span>
        </span>
        <span>·</span>
        <span className={bestWorstItem}>
          {t('statsPanel.worstSlot')}
          <span className={bestWorstValue}>{slotText(worstKey)}</span>
        </span>
      </div>
      <div className={grid}>
        <span />
        {SLOT_LABELS.map((label) => (
          <span key={label} className={colHeader}>
            {label}
          </span>
        ))}

        {DAY_DISPLAY_ORDER.map((day) => (
          <Fragment key={day}>
            <span className={rowHeader}>{dayLabel(day, locale)}</span>
            {SLOT_LABELS.map((_, slot) => {
              const key = `${day}-${slot}`;
              const value = cells.get(key) ?? 0;
              const intensity = value / maxValue;
              const isTop = topKeys.has(key);
              return (
                <Tooltip
                  key={key}
                  label={`${dayLabel(day, locale)} ${SLOT_LABELS[slot]} · ${t(
                    mode === 'sell' ? 'statsPanel.tooltipSales' : 'statsPanel.tooltipAdded',
                    { count: value },
                  )}${isTop ? ` · ${t('statsPanel.topSlot')}` : ''}`}
                >
                  <div
                    className={cellStyle}
                    style={{
                      background: `${heatColor}${alphaHex(intensity)}`,
                      ...(isTop
                        ? { boxShadow: `inset 0 0 0 2px ${heatColor}`, borderColor: 'transparent' }
                        : {}),
                    }}
                  />
                </Tooltip>
              );
            })}
          </Fragment>
        ))}
      </div>

      <div className={legendRow}>
        <span>{t('statsPanel.legendLow')}</span>
        {[0.15, 0.4, 0.65, 0.9].map((intensity) => (
          <span key={intensity} className={legendCell} style={{ background: `${heatColor}${alphaHex(intensity)}` }} />
        ))}
        <span>{t('statsPanel.legendHigh')}</span>
      </div>
    </>
  );
};

// ─── Les deux grilles côte à côte ──────────────────────────────────────────

export const BestMomentsHeatmap: FC<BestMomentsHeatmapProps> = ({ data, loading, variant = 'product' }) => {
  const { t } = useTranslation();

  if (loading || !data || data.length === 0) {
    return (
      <Message type="info" bordered>
        {t('statsPanel.noActivity')}
      </Message>
    );
  }

  // Analyse vendeur : une seule grille — quand le profil met en vente
  // (le rythme de listing, basé sur les ajouts).
  if (variant === 'vendor') {
    return (
      <div className={row}>
        <div className={half}>
          <span className={heatmapTitle}>{t('statsPanel.whenLists')}</span>
          <HeatmapGrid data={data} mode="listing" />
        </div>
      </div>
    );
  }

  return (
    <div className={row}>
      <div className={half}>
        <span className={heatmapTitle}>{t('statsPanel.whenSell')}</span>
        <HeatmapGrid data={data} mode="sell" />
      </div>
      <div className={half}>
        <span className={heatmapTitle}>{t('statsPanel.whenBuy')}</span>
        <HeatmapGrid data={data} mode="buy" />
      </div>
    </div>
  );
};
